// src/lib/conferenciaFaturamento.ts
//
// Conferência do Faturamento: uma linha por UC × mês de referência, partindo da
// planilha de rateio, dizendo se o ciclo foi faturado, não faturado ou não dá para
// verificar. Projeto 12 "Controle de faturas e faturamento", tarefa 5836.
//
// Por que partir do RATEIO e não das faturas: a fila de pendentes
// (PendentesCalculoModal) só enxerga fatura que já chegou. UC do rateio cuja fatura
// nunca chegou não aparece em fila nenhuma — e é assim que o prazo estoura sem
// ninguém perceber.
//
// Regras fechadas com o dono do produto em 15 e 16/09/2026:
//   - Base = UC na planilha de rateio do mês ANTERIOR ao das faturas (rateio de
//     JUL/2026 → faturas de AGO/2026), com QUALQUER status — inclusive "Cancelada",
//     que pode ter saldo de injeção da Vigor a faturar.
//   - Prazo = o mesmo de `janelaFaturamento.ts` (vencimento − 7 dias corridos,
//     ajustado para segunda). Não há regra própria aqui.
//   - "Ativa-S/-Rateio" e "Hsolar Crédito" na coluna da usina são valores válidos
//     da planilha (saldo suficiente / banco de créditos), não número de usina.
//
// Módulo sem I/O: a tela busca, este arquivo decide.

import { codigoMotivoEfetivo, rotuloMotivoPularCalculo, type CodigoMotivoPularCalculo } from './motivoPularCalculo.ts';
import { emissaoBoletoValida } from './emissaoBoletoValida.ts';
import { limiteEmissaoNoPrazo, ehBoletoAvulso } from './janelaFaturamento.ts';
import { addDaysISO, diffInDays } from './janelaCobranca.ts';

// ---------------------------------------------------------------------------
// Tipos de entrada
// ---------------------------------------------------------------------------

export interface ItemRateioConferencia {
  uc: string | null;
  outro_numero_uc: string | null;
  status: string | null;
  nome: string | null;
  cpf_cnpj: string | null;
  uc_geradora: string | null;
}

export interface EmissaoHistorico {
  emitido_em?: string | null;
  emitido_por_nome?: string | null;
  motivo_atraso_emissao?: string | null;
}

export interface FaturaConferencia {
  id: number;
  uc_codigo: string | null;
  uc_codigo_norm: string | null;
  cliente_nome: string | null;
  mes_referencia: string | null;
  classificacao: string | null;
  status_fatura: string | null;
  leitura_anterior: string | null;
  leitura_atual: string | null;
  proxima_leitura: string | null;
  data_apresentacao: string | null;
  data_vencimento: string | null;
  created_at: string | null;
  emitido_em: string | null;
  emitido_por_nome: string | null;
  pular_calculo_em: string | null;
  pular_calculo_por_nome: string | null;
  motivo_pular_calculo: string | null;
  motivo_pular_calculo_codigo: string | null;
  /** Pulo feito sob a regra de injeção (07/10/2026). Ausente nas consultas que não pedem a coluna. */
  pular_calculo_detalhe?: { situacao?: string } | null;
  num_cobranca_boleto: string | number | null;
  linha_digitavel_boleto: string | null;
  url_boleto: string | null;
  empresa_parceira_id: string | number | null;
  status_boleto_vigor: string | null;
  cancelado_em: string | null;
  motivo_atraso_emissao: string | null;
  justificativa_atraso_emissao: string | null;
  energia_injetada_gd1_kwh: number | null;
  saldo_ugs_vigor: number | null;
  historico_emissoes: EmissaoHistorico[] | null;
}

/** Só o que a previsão de chegada precisa da fatura do mês anterior. */
export interface FaturaAnteriorConferencia {
  uc_codigo_norm: string | null;
  proxima_leitura: string | null;
}

// ---------------------------------------------------------------------------
// Tipos de saída
// ---------------------------------------------------------------------------

export type SituacaoFaturamento = 'faturado' | 'nao_faturado' | 'nao_verificavel';

/** O porquê da situação — é o que o filtro e a coluna "Detalhe" mostram. */
export type DetalheConferencia =
  | 'faturado'
  | 'pulado_justificado'
  | 'pulado_em_apuracao'
  | 'aguardando_faturamento'
  | 'boleto_cancelado'
  | 'boleto_sem_registro'
  | 'fatura_nao_recebida'
  | 'fatura_sem_vencimento';

export const ROTULO_SITUACAO: Record<SituacaoFaturamento, string> = {
  faturado: 'Faturado',
  nao_faturado: 'Não faturado',
  nao_verificavel: 'Não verificável',
};

export const ROTULO_DETALHE: Record<DetalheConferencia, string> = {
  faturado: 'Boleto emitido',
  pulado_justificado: 'Pulado com motivo',
  pulado_em_apuracao: 'Em apuração',
  aguardando_faturamento: 'Aguardando faturamento',
  boleto_cancelado: 'Boleto cancelado sem reemissão',
  boleto_sem_registro: 'Boleto sem registro no banco',
  fatura_nao_recebida: 'Fatura não recebida',
  fatura_sem_vencimento: 'Fatura sem vencimento',
};

/** Prazo da linha, na mesma régua de `janelaFaturamento.ts`. */
export type EstadoPrazo =
  | 'no_prazo'          // faturado até o limite
  | 'fora_do_prazo'     // faturado depois do limite
  | 'apos_vencimento'   // faturado depois do vencimento da distribuidora
  | 'aberto'            // ainda não faturado, limite no futuro
  | 'limite_proximo'    // ainda não faturado, limite em até DIAS_ALERTA_LIMITE dias
  | 'limite_vencido'    // ainda não faturado, limite já passou
  | 'sem_prazo';        // sem vencimento para calcular (ou não se aplica)

export interface LinhaConferencia {
  /** Chave estável da UC: `norm_uc` do número usado na fatura, ou do número da planilha. */
  chave: string;
  uc: string;
  outroNumeroUc: string | null;
  cliente: string;
  cpfCnpj: string | null;
  /** Status na planilha de rateio (Ativa, 1° Ciclo, Cancelada...). */
  statusRateio: string;
  /** Números de usina da planilha (uma UC pode aparecer em mais de uma). */
  usinas: string[];
  /** Valores não numéricos da coluna da usina ("Ativa-S/-Rateio", "Hsolar Crédito"). */
  marcacoesRateio: string[];
  situacao: SituacaoFaturamento;
  detalhe: DetalheConferencia;
  /** Fatura que decidiu a linha (a emitida, a pulada, ou a mais recente). */
  faturaId: number | null;
  quantidadeFaturas: number;
  dataVencimento: string | null;
  dataApresentacao: string | null;
  leituraAtual: string | null;
  chegadaEm: string | null;
  limiteEmissao: string | null;
  estadoPrazo: EstadoPrazo;
  /** Faturado: dias depois do limite (>0 = atrasou). Aberto: dias que faltam até o limite. */
  diasPrazo: number | null;
  emitidoEm: string | null;
  emitidoPor: string | null;
  reemitido: boolean;
  /** Emissão fora do prazo com motivo registrado. `null` quando não se aplica. */
  atrasoJustificado: boolean | null;
  motivoAtraso: string | null;
  puladoEm: string | null;
  puladoPor: string | null;
  motivoCodigo: CodigoMotivoPularCalculo | null;
  motivoRotulo: string | null;
  motivoTexto: string | null;
  /** Motivo antigo (texto livre), classificado só na leitura. */
  motivoLegado: boolean;
  /** Sem fatura: previsão de chegada a partir da `proxima_leitura` do mês anterior. */
  chegadaPrevista: string | null;
  chegadaAtrasada: boolean;
}

export interface ResumoConferencia {
  base: number;
  faturado: number;
  naoFaturado: number;
  naoVerificavel: number;
  puladoJustificado: number;
  emApuracao: number;
  naoFaturadoSemJustificativa: number;
  faturaNaoRecebida: number;
  faturaNaoRecebidaAtrasada: number;
  foraDoPrazo: number;
  foraDoPrazoSemJustificativa: number;
  aposVencimento: number;
  limiteProximo: number;
  limiteVencido: number;
  foraDaPlanilha: number;
  porStatusRateio: Record<string, number>;
}

// ---------------------------------------------------------------------------
// Constantes
// ---------------------------------------------------------------------------

/**
 * Dias entre a leitura e a fatura chegar ao sistema, medidos em AGO/2026
 * (`faturas.created_at − leitura_atual`, 1.067 UCs): mediana 7, percentil 90 = 17.
 * A previsão usa a mediana; "atrasada" só depois do p90, para não alarmar à toa.
 */
export const DIAS_LEITURA_ATE_CHEGADA = 7;
export const DIAS_LEITURA_ATE_CHEGADA_P90 = 17;

/** Limite de emissão a esta distância (ou menos) acende o alerta. */
export const DIAS_ALERTA_LIMITE = 2;

/** Placeholder de fatura prevista, sem PDF. Não conta como fatura recebida. */
const STATUS_PLACEHOLDER = 'Aguardando Upload';

// ---------------------------------------------------------------------------
// Utilitários
// ---------------------------------------------------------------------------

/** Mesma regra de `public.norm_uc` e da coluna gerada `faturas.uc_codigo_norm`. */
export const normUc = (v: string | null | undefined): string | null => {
  const s = (v ?? '').replace(/[.\-\s]/g, '').replace(/^0+/, '');
  return s === '' ? null : s;
};

/** Coluna da usina tem número? "Ativa-S/-Rateio" e "Hsolar Crédito" não têm. */
export const ehNumeroUsina = (v: string | null | undefined): boolean => /^\d[\d.\-\s]*$/.test((v ?? '').trim());

const soData = (v: string | null | undefined): string | null => {
  if (!v) return null;
  const s = String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

/** Date local → 'YYYY-MM-DD' (sem passar por UTC, que recua um dia no fuso do Brasil). */
const isoDeData = (d: Date | null): string | null =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : null;

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** Boleto vivo foi cancelado? O status manda; sem status, compara as datas. */
export const boletoCancelado = (f: Pick<FaturaConferencia, 'status_boleto_vigor' | 'cancelado_em' | 'emitido_em'>): boolean => {
  if (f.status_boleto_vigor) return f.status_boleto_vigor === 'Cancelado';
  if (!f.cancelado_em) return false;
  if (!f.emitido_em) return true;
  return new Date(f.cancelado_em).getTime() > new Date(f.emitido_em).getTime();
};

/** Primeira emissão registrada (a original), para medir o prazo sem contar reemissão. */
export const primeiraEmissao = (f: Pick<FaturaConferencia, 'historico_emissoes' | 'emitido_em' | 'emitido_por_nome' | 'motivo_atraso_emissao'>): EmissaoHistorico | null => {
  const hist = (f.historico_emissoes ?? []).filter((h) => h?.emitido_em);
  if (hist.length > 0) {
    return [...hist].sort((a, b) => new Date(a.emitido_em!).getTime() - new Date(b.emitido_em!).getTime())[0];
  }
  return f.emitido_em ? { emitido_em: f.emitido_em, emitido_por_nome: f.emitido_por_nome, motivo_atraso_emissao: f.motivo_atraso_emissao } : null;
};

const faturaValida = (f: FaturaConferencia): boolean =>
  !!f.emitido_em && !boletoCancelado(f) && emissaoBoletoValida(f).ok;

/**
 * Pulado "sem injeção" mas a própria fatura mostra injeção ou saldo das usinas Vigor.
 * Pode ser crédito de outra origem — por isso é "em apuração", não erro.
 *
 * Só vale para os pulos antigos: o pulo com `pular_calculo_detalhe` passou pela trava de
 * injeção (src/lib/pularCalculoInjecao.ts + trg_validar_pulo_calculo), que já separou
 * a injeção da Vigor da de terceiro.
 */
export const puladoEmApuracao = (f: FaturaConferencia, codigo: CodigoMotivoPularCalculo | null): boolean =>
  !f.pular_calculo_detalhe
  && codigo === 'sem_novos_creditos' && (num(f.energia_injetada_gd1_kwh) > 0 || num(f.saldo_ugs_vigor) > 0);

// ---------------------------------------------------------------------------
// Classificação de uma UC
// ---------------------------------------------------------------------------

interface EntradaLinha {
  chave: string;
  itens: ItemRateioConferencia[];
  faturas: FaturaConferencia[];
  faturaAnterior: FaturaAnteriorConferencia | null;
  hojeIso: string;
}

export const classificarLinha = ({ chave, itens, faturas, faturaAnterior, hojeIso }: EntradaLinha): LinhaConferencia => {
  const item = itens[0] ?? null;
  const reais = faturas.filter((f) => f.status_fatura !== STATUS_PLACEHOLDER && !ehBoletoAvulso(f.classificacao));

  const emitida = reais.find(faturaValida) ?? null;
  const pulada = reais.find((f) => !!f.pular_calculo_em) ?? null;
  const maisRecente = [...reais].sort((a, b) => b.id - a.id)[0] ?? null;
  const principal = emitida ?? pulada ?? maisRecente;

  const usinas = Array.from(new Set(itens.map((i) => (i.uc_geradora ?? '').trim()).filter(ehNumeroUsina)));
  const marcacoesRateio = Array.from(new Set(itens.map((i) => (i.uc_geradora ?? '').trim()).filter((v) => v && !ehNumeroUsina(v))));

  const uc = principal?.uc_codigo ?? item?.outro_numero_uc ?? item?.uc ?? chave;
  const outroNumeroUc = [item?.uc, item?.outro_numero_uc].find((n) => n && normUc(n) !== normUc(uc)) ?? null;

  const base: LinhaConferencia = {
    chave,
    uc,
    outroNumeroUc,
    cliente: principal?.cliente_nome ?? item?.nome ?? '',
    cpfCnpj: item?.cpf_cnpj ?? null,
    statusRateio: item ? (item.status?.trim() || '(sem status)') : 'Fora da planilha',
    usinas,
    marcacoesRateio,
    situacao: 'nao_verificavel',
    detalhe: 'fatura_nao_recebida',
    faturaId: principal?.id ?? null,
    quantidadeFaturas: reais.length,
    dataVencimento: principal?.data_vencimento ?? null,
    dataApresentacao: principal?.data_apresentacao ?? null,
    leituraAtual: principal?.leitura_atual ?? null,
    chegadaEm: soData(principal?.created_at),
    limiteEmissao: isoDeData(limiteEmissaoNoPrazo(principal?.data_vencimento)),
    estadoPrazo: 'sem_prazo',
    diasPrazo: null,
    emitidoEm: null,
    emitidoPor: null,
    reemitido: false,
    atrasoJustificado: null,
    motivoAtraso: null,
    puladoEm: null,
    puladoPor: null,
    motivoCodigo: null,
    motivoRotulo: null,
    motivoTexto: null,
    motivoLegado: false,
    chegadaPrevista: null,
    chegadaAtrasada: false,
  };

  // 1) Nenhuma fatura de verdade: não dá para verificar.
  if (!principal) {
    const proxima = faturaAnterior?.proxima_leitura ?? null;
    if (proxima) {
      base.chegadaPrevista = addDaysISO(proxima, DIAS_LEITURA_ATE_CHEGADA) || null;
      const limiteAtraso = addDaysISO(proxima, DIAS_LEITURA_ATE_CHEGADA_P90);
      base.chegadaAtrasada = !!limiteAtraso && hojeIso > limiteAtraso;
    }
    return base;
  }

  // 2) Faturado.
  if (emitida) {
    const original = primeiraEmissao(emitida);
    const emissaoIso = soData(original?.emitido_em);
    base.situacao = 'faturado';
    base.detalhe = 'faturado';
    base.emitidoEm = emitida.emitido_em;
    base.emitidoPor = original?.emitido_por_nome ?? emitida.emitido_por_nome;
    base.reemitido = (emitida.historico_emissoes?.filter((h) => h?.emitido_em).length ?? 0) > 1;

    if (base.limiteEmissao && emissaoIso) {
      const atraso = diffInDays(emissaoIso, base.limiteEmissao);
      base.diasPrazo = atraso;
      if (atraso !== null && atraso > 0) {
        const aposVenc = emitida.data_vencimento ? emissaoIso > emitida.data_vencimento : false;
        base.estadoPrazo = aposVenc ? 'apos_vencimento' : 'fora_do_prazo';
        base.motivoAtraso = original?.motivo_atraso_emissao ?? emitida.motivo_atraso_emissao ?? null;
        base.atrasoJustificado = !!base.motivoAtraso;
      } else {
        base.estadoPrazo = 'no_prazo';
      }
    }
    return base;
  }

  // 3) Pulado.
  if (pulada) {
    const codigo = codigoMotivoEfetivo(pulada);
    base.situacao = 'nao_faturado';
    base.faturaId = pulada.id;
    base.puladoEm = pulada.pular_calculo_em;
    base.puladoPor = pulada.pular_calculo_por_nome;
    base.motivoCodigo = codigo;
    base.motivoRotulo = codigo ? rotuloMotivoPularCalculo(codigo) : null;
    base.motivoTexto = pulada.motivo_pular_calculo;
    base.motivoLegado = !pulada.motivo_pular_calculo_codigo && !!pulada.motivo_pular_calculo;
    base.detalhe = !codigo
      ? 'aguardando_faturamento'
      : puladoEmApuracao(pulada, codigo) ? 'pulado_em_apuracao' : 'pulado_justificado';
    return base;
  }

  // 4) Fatura chegou e não virou boleto. Primeiro, o que houve com o boleto.
  base.situacao = 'nao_faturado';
  if (principal.emitido_em && boletoCancelado(principal)) {
    base.detalhe = 'boleto_cancelado';
  } else if (principal.emitido_em) {
    base.detalhe = 'boleto_sem_registro';
  } else if (!principal.data_vencimento) {
    base.situacao = 'nao_verificavel';
    base.detalhe = 'fatura_sem_vencimento';
    return base;
  } else {
    base.detalhe = 'aguardando_faturamento';
  }

  if (base.limiteEmissao) {
    const faltam = diffInDays(base.limiteEmissao, hojeIso);
    base.diasPrazo = faltam;
    base.estadoPrazo = faltam === null ? 'sem_prazo'
      : faltam < 0 ? 'limite_vencido'
      : faltam <= DIAS_ALERTA_LIMITE ? 'limite_proximo'
      : 'aberto';
  }
  return base;
};

// ---------------------------------------------------------------------------
// Montagem da base
// ---------------------------------------------------------------------------

export interface EntradaConferencia {
  itensRateio: ItemRateioConferencia[];
  faturas: FaturaConferencia[];
  faturasMesAnterior?: FaturaAnteriorConferencia[];
  hojeIso: string;
}

export interface ResultadoConferencia {
  /** UCs da planilha — é a base. */
  linhas: LinhaConferencia[];
  /** UCs com fatura no mês que não estão na planilha. Ficam fora dos totais da base. */
  foraDaPlanilha: LinhaConferencia[];
}

/**
 * Casa planilha e faturas. A UC da planilha pode vir com o número curto, o longo
 * ou os dois; a fatura vem com um deles. Uma UC só vira UMA linha, mesmo que
 * apareça duas vezes na planilha (UC em duas usinas).
 */
export const montarConferencia = ({ itensRateio, faturas, faturasMesAnterior = [], hojeIso }: EntradaConferencia): ResultadoConferencia => {
  const faturasPorChave = new Map<string, FaturaConferencia[]>();
  for (const f of faturas) {
    const k = f.uc_codigo_norm ?? normUc(f.uc_codigo);
    if (!k) continue;
    const lista = faturasPorChave.get(k);
    if (lista) lista.push(f); else faturasPorChave.set(k, [f]);
  }

  const anteriorPorChave = new Map<string, FaturaAnteriorConferencia>();
  for (const f of faturasMesAnterior) {
    if (!f.uc_codigo_norm) continue;
    const atual = anteriorPorChave.get(f.uc_codigo_norm);
    if (!atual || (f.proxima_leitura ?? '') > (atual.proxima_leitura ?? '')) anteriorPorChave.set(f.uc_codigo_norm, f);
  }

  // Agrupa itens da planilha pela UC: qualquer número em comum junta os itens.
  const grupos: { chaves: Set<string>; itens: ItemRateioConferencia[] }[] = [];
  const grupoPorChave = new Map<string, number>();
  for (const item of itensRateio) {
    const chaves = [normUc(item.uc), normUc(item.outro_numero_uc)].filter((k): k is string => !!k);
    if (chaves.length === 0) continue;
    const existente = chaves.map((k) => grupoPorChave.get(k)).find((g) => g !== undefined);
    if (existente !== undefined) {
      grupos[existente].itens.push(item);
      chaves.forEach((k) => { grupos[existente].chaves.add(k); grupoPorChave.set(k, existente); });
    } else {
      const idx = grupos.push({ chaves: new Set(chaves), itens: [item] }) - 1;
      chaves.forEach((k) => grupoPorChave.set(k, idx));
    }
  }

  const usadas = new Set<number>();
  const linhas = grupos.map((g) => {
    const vistas = new Set<number>();
    const doGrupo: FaturaConferencia[] = [];
    let anterior: FaturaAnteriorConferencia | null = null;
    let chaveFatura: string | null = null;
    for (const k of g.chaves) {
      for (const f of faturasPorChave.get(k) ?? []) {
        if (vistas.has(f.id)) continue;
        vistas.add(f.id);
        usadas.add(f.id);
        doGrupo.push(f);
        chaveFatura ??= k;
      }
      anterior ??= anteriorPorChave.get(k) ?? null;
    }
    const chave = chaveFatura ?? [...g.chaves][0];
    return classificarLinha({ chave, itens: g.itens, faturas: doGrupo, faturaAnterior: anterior, hojeIso });
  });

  const sobras = new Map<string, FaturaConferencia[]>();
  for (const f of faturas) {
    if (usadas.has(f.id) || ehBoletoAvulso(f.classificacao)) continue;
    const k = f.uc_codigo_norm ?? normUc(f.uc_codigo);
    if (!k) continue;
    const lista = sobras.get(k);
    if (lista) lista.push(f); else sobras.set(k, [f]);
  }
  const foraDaPlanilha = [...sobras.entries()].map(([chave, fs]) =>
    classificarLinha({ chave, itens: [], faturas: fs, faturaAnterior: null, hojeIso }),
  );

  return { linhas, foraDaPlanilha };
};

export const resumirConferencia = ({ linhas, foraDaPlanilha }: ResultadoConferencia): ResumoConferencia => {
  const r: ResumoConferencia = {
    base: linhas.length,
    faturado: 0,
    naoFaturado: 0,
    naoVerificavel: 0,
    puladoJustificado: 0,
    emApuracao: 0,
    naoFaturadoSemJustificativa: 0,
    faturaNaoRecebida: 0,
    faturaNaoRecebidaAtrasada: 0,
    foraDoPrazo: 0,
    foraDoPrazoSemJustificativa: 0,
    aposVencimento: 0,
    limiteProximo: 0,
    limiteVencido: 0,
    foraDaPlanilha: foraDaPlanilha.length,
    porStatusRateio: {},
  };
  for (const l of linhas) {
    r.porStatusRateio[l.statusRateio] = (r.porStatusRateio[l.statusRateio] ?? 0) + 1;
    if (l.situacao === 'faturado') r.faturado++;
    else if (l.situacao === 'nao_faturado') r.naoFaturado++;
    else r.naoVerificavel++;

    if (l.detalhe === 'pulado_justificado') r.puladoJustificado++;
    if (l.detalhe === 'pulado_em_apuracao') r.emApuracao++;
    if (l.situacao === 'nao_faturado' && l.detalhe !== 'pulado_justificado' && l.detalhe !== 'pulado_em_apuracao') r.naoFaturadoSemJustificativa++;
    if (l.detalhe === 'fatura_nao_recebida') {
      r.faturaNaoRecebida++;
      if (l.chegadaAtrasada) r.faturaNaoRecebidaAtrasada++;
    }
    if (l.estadoPrazo === 'fora_do_prazo' || l.estadoPrazo === 'apos_vencimento') {
      r.foraDoPrazo++;
      if (!l.atrasoJustificado) r.foraDoPrazoSemJustificativa++;
    }
    if (l.estadoPrazo === 'apos_vencimento') r.aposVencimento++;
    if (l.estadoPrazo === 'limite_proximo') r.limiteProximo++;
    if (l.estadoPrazo === 'limite_vencido') r.limiteVencido++;
  }
  return r;
};

// ---------------------------------------------------------------------------
// Mês de referência
// ---------------------------------------------------------------------------

const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

/** 'AGO/2026' → número ordenável (2026*12+7). `null` se o formato não for MMM/AAAA. */
export const ordemMes = (mes: string | null | undefined): number | null => {
  const m = /^([A-Z]{3})\/(\d{4})$/.exec((mes ?? '').trim().toUpperCase());
  if (!m) return null;
  const i = MESES.indexOf(m[1]);
  return i < 0 ? null : Number(m[2]) * 12 + i;
};

export const mesDeOrdem = (ordem: number): string => `${MESES[ordem % 12]}/${Math.floor(ordem / 12)}`;

export const mesAnterior = (mes: string): string | null => {
  const o = ordemMes(mes);
  return o === null ? null : mesDeOrdem(o - 1);
};

/**
 * Mês da planilha de rateio que vale para as faturas de `mesFatura`.
 *
 * O rateio de um mês gera as faturas com referência no mês SEGUINTE: a planilha de
 * JUL/2026 é a base das faturas de AGO/2026 (regra confirmada pelo dono do produto
 * em 16/09/2026). A primeira versão da tela usava a planilha do mesmo mês.
 */
export const mesPlanilhaDasFaturas = (mesFatura: string): string | null => mesAnterior(mesFatura);

/**
 * Qual planilha usar como base. `mes` é o mês DA PLANILHA (use
 * `mesPlanilhaDasFaturas` para chegar nele a partir do mês das faturas). A do próprio
 * mês, se já foi carregada; senão a mais recente anterior a ele. A planilha costuma subir no fim do mês — sem esse recuo,
 * a conferência do mês corrente ficaria vazia justamente quando mais importa.
 *
 * Duas planilhas do mesmo mês: vale a que vier depois na lista (passe ordenado por
 * `created_at`), porque a nova carga substitui a anterior.
 */
export const escolherPlanilhaBase = <T extends { mes_referencia: string }>(planilhas: T[], mes: string): T | null => {
  const alvo = ordemMes(mes);
  if (alvo === null) return null;
  let melhor: T | null = null;
  let melhorOrdem = -Infinity;
  for (const p of planilhas) {
    const o = ordemMes(p.mes_referencia);
    if (o === null || o > alvo) continue;
    if (o >= melhorOrdem) { melhor = p; melhorOrdem = o; }
  }
  return melhor;
};
